import cookie, { CookieAttributes } from "js-cookie";
import { SignoutRedirectArgs, User, UserManager,UserManagerSettings } from "oidc-client-ts";
import React, { useEffect, useRef, useState, useContext, ReactNode } from "react";
import { IUser } from "./models";
import { AuthContextProps, useAuth } from "react-oidc-context";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from 'react-router-dom';
import { removeFromLocalStorage } from "../../helpers/localStorage";
import settings from "../../utilities/settings";
import { checkUserAuth } from "../actions/users";
import { useWeb3Context } from "@w3connector/web3provider";
import { getFormalForbiddenMessage } from "../../helpers/getFormalForbiddenMessage";

interface IUserContext {
	user: IUser | undefined;
	logout: () => void;
	auth: AuthContextProps;
	checkIsAuth: () => Promise<void>;
	isForbidden: boolean;
	isForbiddenMessage: string | null;
}
const UserContext = React.createContext<IUserContext | undefined>(undefined);

// Context
export const useUserContext = () => {
    const ctx = useContext(UserContext);
    if(!ctx) {
        throw new Error('User Context Error');
    }
    return ctx;
}

interface UserProviderProps {
	children: ReactNode
}

// Provider
export const UserProvider:React.FC<UserProviderProps> = ({ children }) => { 
	const [user, setUser] = useState<IUser | undefined>();
	const [isForbidden, setIsForbidden] = useState(false);
	const [isForbiddenMessage, setIsForbiddenMessage] = useState<string | null>(null);
	const kycRedirectDoneRef = useRef(false);
	const auth = useAuth();
	const navigate = useNavigate();
	const {  
        modalOpen,
        connectors,
        web3Error,
        web3Hooks,
        chainName,
        setModalOpen,
        setWeb3Error,
        handleDisconnect } = useWeb3Context();

	const parseClaimBool = (value: unknown): boolean => {
		if (value === 'True' || value === true) return true;
		if (value === 'False' || value === false) return false;
		return false;
	};

	const setAccessToken = (token: string) => {
		const decoded = jwtDecode<IUser>(token);
		if (decoded.privateInfo) decoded.privateInfo = JSON.parse(decoded.privateInfo as unknown as string);
		if (decoded.companyAssociationInfo) decoded.companyAssociationInfo = JSON.parse(decoded.companyAssociationInfo as unknown as string);
		if (decoded['is-national-entity'] !== undefined) {
			decoded['is-national-entity'] = parseClaimBool(decoded['is-national-entity']);
		}
		setUser(decoded);
		cookie.set("Authorization", "Bearer " + token);
	};

	const logout = async () => {
		if(web3Hooks.isActive) await handleDisconnect().finally();
		try { await auth.signoutSilent(); } catch {}
		auth.removeUser();
		cookie.remove("Authorization");
		cookie.remove("idsrv.session");
		cookie.remove(".AspNetCore.Identity.Application");
		removeFromLocalStorage('oidc.user:'+settings.Domains.IdentityClient+":"+settings.oidc.clientId);
		setUser(undefined);
		navigate("/");
	};
	
	useEffect(() => {
		if (auth.isAuthenticated && auth.user && !auth.user.expired) {
		  	setAccessToken(auth.user.access_token);
		}
	
		const onUserLoaded = (user: any) => {
		  	if(user && user.access_token) setAccessToken(user.access_token);
		};
	
		const onAccessTokenExpired = async () => {
			try {
					const renewedUser = await auth.signinSilent();
					if(renewedUser?.access_token) setAccessToken(renewedUser.access_token);
				} catch (e) {
					console.error("Silent renew fallito", e);
					await logout();
				}
			};
	
		auth.events.addUserLoaded(onUserLoaded);
		auth.events.addAccessTokenExpired(onAccessTokenExpired);
	
		return () => {
			auth.events.removeUserLoaded(onUserLoaded);
			auth.events.removeAccessTokenExpired(onAccessTokenExpired);
		};
	}, [auth]);
	

	const checkIsAuth = async () => {  
		try {
			await checkUserAuth();
			setIsForbidden(false);
			setIsForbiddenMessage(null);
		} catch (e: any) {
			if (e.response?.status === 401) {
				setIsForbidden(false);
				setIsForbiddenMessage(null);
		
				if (auth.isLoading) return;
				if (auth.user && !auth.user.expired) return;
		
				await logout();
			}
			if (e.response?.status === 403) {
				const msgKey = e.response?.data?.message || e.response?.data?.error || e.response?.data || "Accesso negato";
				setIsForbidden(true);
				if (msgKey === "KYC_STATUS_NOT_APPROVED") {
					const kycStatus = e.response?.data?.kycStatus || "Status non disponibile";
					const communicationHashedToken = e.response?.data?.kyc?.communicationHashedToken ?? null;
					setIsForbiddenMessage(getFormalForbiddenMessage(kycStatus));
					if (communicationHashedToken && !kycRedirectDoneRef.current) {
						kycRedirectDoneRef.current = true;
						window.location.href = `${settings.PortaleKYC.Url}/kyc/verify?token=${communicationHashedToken}`;
					}
				} else {
					setIsForbiddenMessage(getFormalForbiddenMessage(msgKey));
				}
			}
		}
	};

	return (
		<UserContext.Provider value={{user, logout, auth, checkIsAuth, isForbidden, isForbiddenMessage}}>
			{children}
		</UserContext.Provider>
	);
};